import { action, makeObservable, observable, reaction } from 'mobx';
import { InputBaseModel } from '../models';
import { InputUtils } from '../misc';
import { InputChangeType } from '../types';
import { Disposer } from '../../custom_shared/misc';

export default class InputChangeTracker extends Disposer {
    changeType?: InputChangeType;

    constructor(
        readonly inputId: string,
        readonly newInput?: InputBaseModel,
        readonly previousInput?: InputBaseModel
    ) {
        super();

        makeObservable(this, {
            changeType: observable,
            updateChangeType: action
        });

        this.updateChangeType();
        this.initializeChangeTypeReaction();
    }

    get inputName(): string {
        return this.newInput?.name ?? this.previousInput?.name ?? this.inputId;
    }

    private static getChangeType = (
        newInput?: InputBaseModel,
        previousInput?: InputBaseModel
    ): InputChangeType | undefined => {
        if (newInput?.hasUnsavedChanges) {
            return 'unsaved';
        }

        const hasNewValue = InputChangeTracker.hasComparableValue(newInput);
        const hasPreviousValue = InputChangeTracker.hasComparableValue(previousInput);

        if (hasNewValue && !hasPreviousValue) {
            return 'added';
        }

        if (hasPreviousValue && !hasNewValue) {
            return 'removed';
        }

        if (!hasNewValue && !hasPreviousValue) {
            return undefined;
        }

        if (!newInput || !previousInput) {
            return undefined;
        }

        if (InputUtils.areValuesEqual(newInput.dataType, newInput.value, previousInput.value)) {
            return undefined;
        }

        return 'modified';
    };

    private static hasComparableValue(input?: InputBaseModel): boolean {
        return input?.hasValue ?? false;
    }

    updateChangeType() {
        this.changeType = InputChangeTracker.getChangeType(this.newInput, this.previousInput);
    }

    dispose() {
        this.disposeReactions();
    }

    private initializeChangeTypeReaction() {
        if (!this.newInput && !this.previousInput) {
            return;
        }

        this.reactions.push(
            reaction(
                () => [this.newInput?.value, this.previousInput?.value, this.newInput?.hasUnsavedChanges],
                () => this.updateChangeType()
            )
        );
    }
}
